import type { PropsWithChildren, KeyboardEvent, ForwardedRef } from "react";
import styled from "styled-components";
import { useIntl } from "react-intl";
import { animated } from "@react-spring/web";
import { CloseButton } from "src/auth/components/CloseButton/CloseButton";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import {
  type iconSize,
  color,
  fontSize,
  fontWeight,
  spacing,
  textColor,
  lineHeight,
} from "../../theme";
import { Modal } from "../Modal/Modal";
import { Slide } from "../Transition/Slide";
import messages from "./Drawer.messages";

type DrawerProps = PropsWithChildren<{
  onBackdropClicked: () => void;
  onCloseClicked: () => void;
  title?: string;
  isOpen: boolean;
  anchor?: Anchor;
  hasPadding?: boolean;
  hasRoundedCorners?: boolean;
  hideCloseButton?: boolean;
  height?: string;
  heightPercentage?: string;
  closeSize?: keyof typeof iconSize;
  greyBackground?: boolean;
  contentRef?: ForwardedRef<HTMLDivElement>;
  order?: number;
  disableAnimation?: boolean;
}>;

type Anchor = "top" | "bottom" | "right";

export function Drawer({
  onBackdropClicked,
  onCloseClicked,
  title,
  isOpen,
  anchor,
  hasPadding,
  hasRoundedCorners,
  hideCloseButton,
  height,
  heightPercentage,
  closeSize,
  greyBackground,
  contentRef,
  order,
  children,
  disableAnimation,
}: DrawerProps) {
  const intl = useIntl();

  const anchorValue = anchor ?? "bottom";
  const paddingExists = hasPadding ?? true;
  const roundedCornersExist = hasRoundedCorners ?? true;

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    switch (event.key) {
      case "Enter":
        onCloseClicked();
        break;
    }
  }

  return (
    <Modal order={order} open={isOpen} onBackdropClicked={onBackdropClicked}>
      <Slide
        open={isOpen}
        anchor={anchorValue}
        config={{ duration: disableAnimation ? 0 : undefined }}
      >
        <DrawerBackground
          data-testid="drawer"
          anchor={anchorValue}
          hasRoundedCorners={roundedCornersExist}
          height={height}
          heightPercentage={heightPercentage}
          greyBackground={greyBackground}
        >
          <DrawerContent hasPadding={paddingExists} ref={contentRef}>
            {hideCloseButton ? (
              <VisuallyHidden>
                <button onKeyDown={handleKeyDown}>
                  {intl.formatMessage(messages.close)}
                </button>
              </VisuallyHidden>
            ) : (
              <DrawerCloseButton
                size={closeSize ?? "lg"}
                onClose={onCloseClicked}
              />
            )}
            {title && <Title>{title}</Title>}
            <Content>{children}</Content>
          </DrawerContent>
        </DrawerBackground>
      </Slide>
    </Modal>
  );
}

const DrawerBackground = animated(styled.div<{
  anchor: Anchor;
  hasRoundedCorners: boolean;
  height?: string;
  tripPlannerHeight?: boolean;
  greyBackground?: boolean;
  heightPercentage?: string;
}>`
  background-color: ${(props) =>
    props.greyBackground ? color.grey0 : color.white};
  position: fixed;
  color: ${textColor.primaryOnLight};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;

  ${(props) => {
    switch (props.anchor) {
      case "top":
        return `
          top: 0;
          max-height: 80%;
          width: 100%;
          `;
      case "bottom":
        return `
          bottom: 0;
          min-height: 40%;
          max-height: 80%;
          width: 100%;
          `;
      case "right":
        return `
          top: 0;
          right: 0;
          height: 100%;
          min-width: 40%;
          max-width: 80%;
          `;
    }
  }};

  ${({ height }) => !!height && `height:${height}; min-height: 50%;`}

  ${({ heightPercentage }) =>
    !!heightPercentage && `height:${heightPercentage}%; min-height: 50%;`}

    ${({ heightPercentage }) =>
    !!heightPercentage && `max-height:${heightPercentage}%; min-height: 50%;`}

  ${(props) => {
    if (props.hasRoundedCorners) {
      switch (props.anchor) {
        case "top":
          return `border-radius: 0 0 24px 24px;`;
        case "bottom":
          return `border-radius: 24px 24px 0 0;`;
        case "right":
          return `border-radius: 24px 0 0 24px;`;
      }
    }
  }}
`);

const DrawerContent = styled.div<{ hasPadding: boolean }>`
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  ${(props) => {
    if (props.hasPadding) {
      return `
        padding-bottom: ${spacing.xl};
        padding-top: ${spacing.xl};
      `;
    }
  }}
`;

const Title = styled.h2`
  font-weight: ${fontWeight.medium};
  font-size: ${fontSize.h4};
  padding-left: ${spacing.xl};
  padding-top: ${spacing.xl};
  padding-right: ${spacing.xl};
`;

const Content = styled.div`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.normal};
`;

type DrawerCloseButtonType = {
  onClose: () => void;
  size: keyof typeof iconSize;
};

export function DrawerCloseButton({ onClose }: DrawerCloseButtonType) {
  return (
    <CloseButtonWrapper>
      <CloseButton onClick={onClose} />
    </CloseButtonWrapper>
  );
}

const CloseButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
